import { Box, Button, Tabs, Tab, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { FieldAlias } from "../../interfaces/field";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import SearchResultGrid from "../searchResultGrid/searchResultGrid";
import filterSectionStyle from "../../styles/filterSectionStyle";
import fieldAliasColumnDef from "../../colDef/fieldAliasColumnDef";
import ConfigureFieldAliasModal from "../modals/configureFieldAliasModal";
import ConfigureFieldSecurityModal from "../modals/configureFieldSecurityModal";
import FieldValueMappingModal from "../modals/fieldValueMappingModal";
import FieldAliasService from "../../services/fieldAliasService";


const ManageFieldAlias: React.FC = () => {
    const [fieldAliases, setFieldAliases] = useState<FieldAlias[]>([]);
    const picklists = useSelector((state: RootState) => state.picklists.picklists);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [openSecurityModal, setOpenSecurityModal] = useState<boolean>(false);
    const [openValueMappingModal, setOpenValueMappingModal] = useState<boolean>(false);
    const [selectedFieldAliases, setSelectedFieldAliases] = useState<FieldAlias[]>([]);
    const [selectedLoanType, setSelectedLoanType] = useState<string>('');
    const [loanTypes, setLoanTypes] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    // Get available loan types from picklist
    useEffect(() => {
        const availableLoanTypes = picklists.LOAN_TYPE || [];
        setLoanTypes(availableLoanTypes);
        if (availableLoanTypes.length > 0 && !selectedLoanType) {
            setSelectedLoanType(availableLoanTypes[0]);
        }
    }, [picklists]);

    // Load existing field aliases when loan type changes
    useEffect(() => {
        if (selectedLoanType) {
            fetchExistingFieldAliases();
        }
    }, [selectedLoanType]);

    const fetchExistingFieldAliases = async () => {
        if (!selectedLoanType) return;
        
        setLoading(true);
        try {
            const fieldAliasesData = await FieldAliasService.getFieldAliases(selectedLoanType);
            setFieldAliases(fieldAliasesData);
        } catch (error) {
            console.error('Error fetching field aliases:', error);
        } finally {
            setLoading(false);
        }
    };

    // Handle modal open/close
    const handleOpenModal = () => {
        setOpenModal(true);
    };
    
    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Handle security modal
    const handleOpenSecurityModal = () => {
        setOpenSecurityModal(true);
    };

    const handleCloseSecurityModal = () => {
        setOpenSecurityModal(false);
    };

    // Handle value mapping modal
    const handleOpenValueMappingModal = () => {
        setOpenValueMappingModal(true);
    };

    const handleCloseValueMappingModal = () => {
        setOpenValueMappingModal(false);
    };

    // Handle loan type tab change
    const handleLoanTypeChange = (event: React.SyntheticEvent, newValue: string) => {
        setSelectedLoanType(newValue);
        setSelectedFieldAliases([]); // Clear selection when changing loan type
    };

    // Handle row selection
    const handleRowSelection = (ids: any) => {
        const selectionArray = Array.from(ids.ids);
        const selected = fieldAliases.filter(alias => 
            selectionArray.includes(alias.field_alias_id)
        );
        setSelectedFieldAliases(selected);
    };


    if (loanTypes.length === 0) {
        return (
            <Box m={2}>
                <Typography variant="h6" color="error">
                    No loan types available. Please ensure loan types are configured in the system.
                </Typography>
            </Box>
        );
    }

    return (
        <Box m={2}>
            {/* Loan Type Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs 
                    value={selectedLoanType} 
                    onChange={handleLoanTypeChange}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {loanTypes.map((loanType) => (
                        <Tab 
                            key={loanType} 
                            label={loanType} 
                            value={loanType}
                        />
                    ))}
                </Tabs>
            </Box>

            <Box sx={filterSectionStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        Field Aliases for {selectedLoanType}
                    </Typography>
                    <Box display="flex" gap={2}>
                    {selectedFieldAliases.length === 1 && (
                        <Button
                            variant="outlined"
                            onClick={handleOpenSecurityModal}
                            color="secondary"
                        >
                            Configure Field Access
                        </Button>
                    )}
                        <Button
                            variant="outlined"
                            onClick={handleOpenValueMappingModal}
                            color="info"
                        >
                            Configure DPD Bucket Mappings
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleOpenModal}
                        >
                            Configure Field Aliases
                        </Button>
                    </Box>
                </Box>
            </Box>

            <SearchResultGrid
                height='72vh'
                getRowId={(row: FieldAlias) => row.field_alias_id}
                results={fieldAliases}
                onSelectRow={handleRowSelection}
                columns={fieldAliasColumnDef}
                isRowSelectionAllowed={true}
            />

            {/* Configure Field Alias Modal */}
            {selectedLoanType && (
                <ConfigureFieldAliasModal
                    open={openModal}
                    onClose={handleCloseModal}
                    initialFieldAliases={fieldAliases}
                    loanType={selectedLoanType}
                    onSave={fetchExistingFieldAliases}
                />
            )}

            {/* Configure Field Security Modal */}
            {selectedFieldAliases.length === 1 && (
                <ConfigureFieldSecurityModal
                    open={openSecurityModal}
                    onClose={handleCloseSecurityModal}
                    selectedFieldAlias={selectedFieldAliases[0]}
                    onSave={() => {
                        // No need to refresh field aliases, just close modal
                        handleCloseSecurityModal();
                    }}
                />
            )}

            {/* Field Value Mapping Modal */}
            {selectedLoanType && (
                <FieldValueMappingModal
                    open={openValueMappingModal}
                    onClose={handleCloseValueMappingModal}
                    loanType={selectedLoanType}
                />
            )}
        </Box>
    );
}

export default ManageFieldAlias;
